import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { resetPassword } from '@/api/auth';
import { useFormErrors } from '@/hooks/useFormErrors';

/**
 * Choose a new password using an emailed token — BUILD-PLAN §17.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fieldErrors, formError, handleError, reset } = useFormErrors();

  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';

  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  // A link that lost its parameters cannot be completed, so say so rather
  // than presenting a form that is guaranteed to fail (§41).
  if (!token || !email) {
    return (
      <AuthLayout
        title="This link isn’t valid"
        footer={
          <Link to="/forgot-password" className="text-accent underline underline-offset-4">
            Request a new link
          </Link>
        }
      >
        <Alert tone="error">
          This password reset link is incomplete or has been altered. Please request a new one.
        </Alert>
      </AuthLayout>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    reset();
    setSubmitting(true);

    try {
      await resetPassword({
        token,
        email,
        password,
        password_confirmation: confirmation,
      });

      navigate('/login?reset=1', { replace: true });
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Choose a new password"
      description={`Setting a new password for ${email}.`}
      footer={
        <Link to="/login" className="text-accent underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        {formError && <Alert tone="error">{formError}</Alert>}

        <Input
          label="New password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors['password']}
          autoComplete="new-password"
          hint="At least 12 characters, with upper and lower case letters and a number."
          required
        />

        <Input
          label="Confirm new password"
          type="password"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          error={fieldErrors['password_confirmation']}
          autoComplete="new-password"
          required
        />

        <Button type="submit" loading={submitting} fullWidth size="lg">
          Reset Password
        </Button>
      </form>
    </AuthLayout>
  );
}
